import { CanopyProvider } from '@bloomfieldrobotics/canopy';
import { Box, Stack, Typography } from '@mui/material';
import { useUrlReduxBidirectionalSync } from '../../hooks/useUrlReduxBidirectionalSync';
import { useUserCropTypes } from '../../services/entities/hooks/settings/useUserCropTypes';
import { useFeatureFlags } from '../../services/featureFlags/hooks/useFeatureFlags';
import { useTranslation } from '../../services/translation/hooks/useTranslation';
import { BetaWrapper } from '../commonUI/BetaWrapper/BetaWrapper';
import { CustomerDropdown } from '../commonUI/dropdowns/CustomerDropdown';
import {
  selectCurrCustomerID,
  setCurrCustomerByID,
} from '../select/selectSlice';
import { BlueberryReportsList } from './blueberry/components/BlueberryReportsList';
import { GrapeReportsList } from './grape/components/GrapeReportsList';
import { useReportsLandingPageParams } from './hooks/useReportsLandingPageParams';

export const ReportsLandingPage = () => {
  const { t } = useTranslation();

  // URL parameters for Reports Landing Page
  const { params, updateParams } = useReportsLandingPageParams();
  const { customerID } = params;

  // Bidirectional sync: URL ⟷ Redux for customerID using the proper hook
  useUrlReduxBidirectionalSync({
    urlValue: customerID,
    updateUrl: (value: string) => updateParams({ customerID: value }),
    reduxSelector: selectCurrCustomerID,
    reduxAction: setCurrCustomerByID,
    defaultValue: '', // Empty string default
  });

  const setCustomerId = (newCustomerID: string) => {
    updateParams({ customerID: newCustomerID });
  };

  const userCropTypes = useUserCropTypes();
  const { GrapeClusterReport } = useFeatureFlags();

  return (
    <CanopyProvider>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          overflowY: 'scroll',
        }}
      >
        <Stack
          sx={{
            padding: 6,
            gap: 6,
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            width: '80%',
            minWidth: 'min-content',
            height: 'min-content',
          }}
        >
          <BetaWrapper>
            <Typography
              sx={{
                fontSize: '1.3125rem',
                fontWeight: 600,
                lineHeight: '120%',
              }}
            >
              {t('Reports')}
            </Typography>
          </BetaWrapper>
          <CustomerDropdown value={customerID} onChange={setCustomerId} />
          {!!customerID && (
            <>
              {userCropTypes.blueberries && (
                <BlueberryReportsList customerId={customerID} />
              )}
              {GrapeClusterReport && userCropTypes.grapes && (
                <GrapeReportsList customerID={customerID} />
              )}
            </>
          )}
        </Stack>
      </Box>
    </CanopyProvider>
  );
};
